import type { Dayjs } from "dayjs";
import dayjs from "../dayjsConfig.js";

// The money formulas that used to live here now live in
// packages/server/src/api/utils/balanceFormulas.ts and are not re-exported: no
// client code should be able to reach a balance formula by accident. What is
// left below is dates and formatting, which carry no business meaning and are
// safe to duplicate on the server side.

export function getTotalDaysOfMonth(
	year: string,
	monthNumber: number,
): number[] {
	const stringMonth = monthNumber < 9 ? `0${monthNumber}` : monthNumber;
	const totalDays = [];
	for (
		let i = 1;
		i <= getMomentUTC(`${year}-${stringMonth}`, "YYYY-MM").daysInMonth();
		i++
	) {
		totalDays.push(i);
	}
	return totalDays;
}

export function getStringMonthNumber(monthNumber: number): string {
	return monthNumber < 10 ? `0${monthNumber}` : String(monthNumber);
}

export function getSumFromArray(arrayOfNumbers: number[]): number {
	return arrayOfNumbers.reduce((sum, current) => sum + current, 0);
}

export function getMiddleValueFromArray(arrayOfNumbers: number[]) {
	const sum = getSumFromArray(arrayOfNumbers);
	if (arrayOfNumbers.length === 0) {
		return 0;
	}
	return Math.round(sum / arrayOfNumbers.length);
}

export function calculatePercentDifference(
	currentSum: number,
	previousSum: number,
): number {
	if (currentSum === 0 && previousSum === 0) {
		return 0;
	}

	const difference =
		currentSum > previousSum
			? ((currentSum - previousSum) * 100) / currentSum
			: ((previousSum - currentSum) * 100) / previousSum;

	if (Number.isNaN(difference)) {
		return 0;
	}

	const result = Number.parseFloat(difference.toFixed(1));

	return result % 1 === 0 ? Math.round(result) : result;
}

export function getNumberWithHundreds(number: number) {
	return Number(number?.toFixed(2));
}

// Preserves original moment(date, format).utc() semantics:
// parse in local timezone first, then convert to UTC.
export const getMomentUTC = (
	date: string | Date | Dayjs | undefined = undefined,
	format: string | undefined = undefined,
) => (format ? dayjs(date, format).utc() : dayjs(date).utc());

export function getStartOfPreviousDayInUTC() {
	return getMomentUTC().subtract(1, "day").startOf("day");
}

// Takes whatever the callers hold: the date pickers work in Dayjs, the stored
// values arrive as strings. dayjs.utc() accepts all three, and declaring only
// `string` here was a fiction every call site had to ignore.
export function convertDateToIsoString(selectedDate: string | Dayjs | Date) {
	return dayjs.utc(selectedDate).startOf("day").format();
}
